import { amplifyClient, getEndpointV2 } from 'api';
import { fromEntries } from 'helpers/general';
import { all, call, Effect, select } from 'redux-saga/effects';
import { userFetch } from 'store/features/userFetch';
import { ApiError, UserData, UserRfcConfigConfiguration, UserRfcConfigType } from 'types';

import { selectPath } from './selectPath';
import { uploadRfcFile } from './uploadRfcFile';

export interface UpdateAddReturn {
  error: ApiError | null;
}

const paths = {
  efirm: '/auth/company/:companyId/rfc/:code/fielConfig',
  csd: '/auth/company/:companyId/rfc/:code/csdConfig',
} as Record<UserRfcConfigType, string>;

function* uploadRfcConfig({
  type,
  config,
  code,
}: {
  type: UserRfcConfigType;
  config: UserRfcConfigConfiguration;
  code: string;
}): Generator<unknown, UserRfcConfigConfiguration> {
  const effects: Array<Effect> = [];
  const keys: Array<string> = [];

  if (config.certifier instanceof File) {
    keys.push('certifier');
    effects.push(call(uploadRfcFile, { code, type, file: config.certifier }));
  }
  if (config.publicKey instanceof File) {
    keys.push('publicKey');
    effects.push(call(uploadRfcFile, { code, type, file: config.publicKey }));
  }

  const keyValues = (yield all(effects)) as Array<string>;

  const out: UserRfcConfigConfiguration = {
    ...config,
    ...fromEntries(keys.map((key, index) => [key, keyValues[index]])),
  };

  return out;
}

export function* addConfig({
  config,
  type,
  code,
}: {
  config: UserRfcConfigConfiguration;
  type: UserRfcConfigType;
  code: string;
}): Generator<unknown, UpdateAddReturn> {
  const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;

  try {
    const configuration = (yield call(uploadRfcConfig, {
      config,
      type,
      code,
    })) as UserRfcConfigConfiguration;

    const path = selectPath(paths, type);

    yield call(amplifyClient, {
      method: 'post',
      url: getEndpointV2({ path, urlParams: { companyId, code } }),
      data: {
        configuration,
      },
    });

    return {
      error: null,
    };
  } catch (e) {
    return {
      error: e as ApiError,
    };
  }
}

export function* updateConfig({
  config,
  type,
  code,
}: {
  config: UserRfcConfigConfiguration;
  type: UserRfcConfigType;
  code: string;
}): Generator<unknown, UpdateAddReturn> {
  const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;

  try {
    const configuration = (yield call(uploadRfcConfig, {
      config,
      type,
      code,
    })) as UserRfcConfigConfiguration;

    const path = selectPath(paths, type);

    yield call(amplifyClient, {
      method: 'put',
      url: getEndpointV2({ path, urlParams: { companyId, code } }),
      data: {
        configuration,
      },
    });

    return {
      error: null,
    };
  } catch (e) {
    return {
      error: e as ApiError,
    };
  }
}

export function* removeConfig({
  type,
  code,
}: {
  type: UserRfcConfigType;
  code: string;
}): Generator<unknown, undefined> {
  const { companyId } = (yield select(userFetch.selectors.getData)) as UserData;

  const path = selectPath(paths, type);

  yield call(amplifyClient, {
    method: 'del',
    url: getEndpointV2({ path, urlParams: { companyId, code } }),
  });

  return;
}
